/*
 * Timeline decade — line draw-in + jackpot year roll.
 *
 * Wraps the "line | year | line" row. The first time it scrolls into view:
 *   - the two hand-drawn lines grow outward from the year (CSS, via .is-drawn);
 *   - a numeric year spins up like a slot machine — each digit is a reel that
 *     scrolls a 0–9 strip and settles on its target digit, later digits landing
 *     last for the "jackpot" cascade.
 *
 * Arming/reels are JS-only, so with JS off the lines and the plain year render
 * normally; reduced motion shows everything in its final state without motion.
 */
const SPIN_BASE_MS = 1100; // duration of the first (leftmost) reel
const SPIN_STAGGER_MS = 350; // each subsequent reel takes this much longer
const SPIN_BASE_TURNS = 3; // full 0–9 cycles before the first reel settles
const SPIN_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

class FluevogDecadeLines extends HTMLElement {
  connectedCallback() {
    const animate = this.dataset.animate === 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!animate || !('IntersectionObserver' in window)) return;

    this.classList.add('is-armed');
    this.reels = this.buildReels();

    if (reducedMotion) {
      this.classList.add('is-drawn');
      this.settle();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.classList.add('is-drawn');
          this.spin();
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.2 }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /*
   * Replace a purely numeric year with one reel per digit. Returns the reel
   * descriptors, or null when the year isn't numeric (left as plain text).
   * Reels start at 0 (so the year reads 0000 before it spins).
   */
  buildReels() {
    const yearEl = this.querySelector('.fvtl-decade__year');
    if (!yearEl) return null;

    const value = (yearEl.dataset.year || yearEl.textContent || '').trim();
    if (!/^\d+$/.test(value)) return null;

    yearEl.classList.add('is-reels');
    yearEl.textContent = '';

    const reels = value.split('').map((char, index) => {
      const target = parseInt(char, 10);
      const turns = SPIN_BASE_TURNS + index; // later digits spin more → settle last
      const total = turns * 10 + target;

      const reel = document.createElement('span');
      reel.className = 'fvtl-year__reel';
      const strip = document.createElement('span');
      strip.className = 'fvtl-year__strip';

      for (let n = 0; n <= total; n += 1) {
        const cell = document.createElement('span');
        cell.textContent = String(n % 10);
        strip.appendChild(cell);
      }

      // Start at 0 — the year reads 0000 until it spins up.
      reel.appendChild(strip);
      yearEl.appendChild(reel);

      return { strip, total, index };
    });

    return reels;
  }

  /* Jump straight to the final year with no motion (reduced-motion path). */
  settle() {
    if (!this.reels) return;
    this.reels.forEach(({ strip, total }) => {
      strip.style.transition = 'none';
      strip.style.transform = `translateY(-${total}em)`;
    });
  }

  spin() {
    if (!this.reels) return;

    this.reels.forEach(({ strip, total, index }) => {
      strip.style.transition = 'none';
      strip.style.transform = 'translateY(0)';
      // Force reflow so the jump to 0 is committed before the eased run.
      void strip.getBoundingClientRect();

      const duration = SPIN_BASE_MS + index * SPIN_STAGGER_MS;
      requestAnimationFrame(() => {
        strip.style.transition = `transform ${duration}ms ${SPIN_EASING}`;
        strip.style.transform = `translateY(-${total}em)`;
      });
    });
  }
}

if (!customElements.get('fv-decade-lines')) {
  customElements.define('fv-decade-lines', FluevogDecadeLines);
}

/*
 * Gentle parallax for each decade's framed photo. The frame drifts vertically by
 * a fraction of its scroll progress (set as --fvtl-frame-shift, consumed by the
 * frame's transform). One shared rAF-throttled loop drives every on-screen frame.
 */
function initDecadeParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const frames = Array.from(
    document.querySelectorAll('.fvtl-decade__frame[data-parallax="true"]')
  );
  if (frames.length === 0) return;

  const visible = new Set();
  let ticking = false;

  const update = () => {
    ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;

    visible.forEach((frame) => {
      const strength = parseFloat(frame.dataset.parallaxStrength || '30');
      const rect = frame.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      // -1 entering from the bottom, 0 centered, +1 leaving at the top.
      const progress = (center - vh / 2) / ((vh + rect.height) / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      frame.style.setProperty('--fvtl-frame-shift', `${(-clamped * strength).toFixed(2)}px`);
    });
  };

  const onScroll = () => {
    if (ticking || visible.size === 0) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      onScroll();
    },
    { rootMargin: '10% 0px' }
  );
  frames.forEach((frame) => observer.observe(frame));

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
}

/*
 * Layered scrapbook parallax for the story photos. Each moment photo drifts
 * vertically at its own gentle rate and direction (alternating, so the collage
 * feels like loosely pinned layers rather than one flat plane). The drift is
 * written to the `translate` property so it composes with each photo's static
 * `rotate` tilt without overriding it. One shared rAF loop drives them all.
 */
function initMomentParallax() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const items = Array.from(document.querySelectorAll('.fvtl-decade__moment-media'));
  if (items.length === 0) return;

  // Subtle, varied drift (px) — alternating sign for a pinned-layers feel.
  const depths = [18, -26, 30, -16, 22, -28];
  items.forEach((el, i) => {
    el.dataset.depth = depths[i % depths.length];
  });

  const visible = new Set();
  let ticking = false;

  const update = () => {
    ticking = false;
    const vh = window.innerHeight || document.documentElement.clientHeight;

    visible.forEach((el) => {
      const strength = parseFloat(el.dataset.depth || '20');
      const rect = el.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      // -1 entering from the bottom, 0 centered, +1 leaving at the top.
      const progress = (center - vh / 2) / ((vh + rect.height) / 2);
      const clamped = Math.max(-1, Math.min(1, progress));
      el.style.translate = `0 ${(-clamped * strength).toFixed(2)}px`;
    });
  };

  const onScroll = () => {
    if (ticking || visible.size === 0) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) visible.add(entry.target);
        else visible.delete(entry.target);
      });
      onScroll();
    },
    { rootMargin: '12% 0px' }
  );
  items.forEach((el) => observer.observe(el));

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  update();
}

function initDecadeMotion() {
  initDecadeParallax();
  initMomentParallax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDecadeMotion);
} else {
  initDecadeMotion();
}
